import React, { useEffect, useState } from "react";
import {
  Calendar,
  ChevronDown,
  Clock,
  Filter,
  Mail,
  MapPinCheckInside,
  Phone,
  UserPlus,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import VoiceUpdateButton from "../VoiceUpdateButton.jsx";
import { Badge } from "../ui/Badge.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card.jsx";
import { fetchStages } from "../../services/leadStageService.js";

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return `${Math.floor(diff / 604800000)}w ago`;
};

const formatCreatedDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
};

const DashboardLeadCard = ({
  activeDealStatus,
  activeStatus,
  label,
  count,
  dealStatusDropdownRef,
  dateDealStatusFilter,
  setDealStatusDropdownOpen,
  DEAL_STATUS_LABELS,
  dealStatusDropdownOpen,
  setDateDealStatusFilter,
  setCalendarOpenedOnce,
  setOpenCalendar,
  leads,
  error,
  isLoading,
  leadsRef,
  getSnapshot,
  saveCache,
  DASH_SCROLL_KEY,
  handleLeadClick,
  hasMore,
  isFetchingMore,
  sentinelCallbackRef,
  setIsAssignModalOpen,
  setSelectedLead,
  assignScrollLeadId,
}) => {
  const [stages, setStages] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchStages();
        setStages(res.data || []);
      } catch {
        setStages([]);
      }
    })();
  }, []);

  // Build label map from dynamic stages (fallback to DEAL_STATUS_LABELS prop)
  const stageLabels =
    stages.length > 0
      ? stages.reduce((acc, s) => {
          acc[s.stage_key] = s.stage_label;
          return acc;
        }, {})
      : DEAL_STATUS_LABELS;

  return (
    <Card className="!bg-transparent !border-none !shadow-none !px-0">
      <CardHeader className="!py-0">
        {(() => {
          const isTodayView = !activeDealStatus && activeStatus !== "pending";

          return (
            <div
              className={`w-full flex gap-2 ${
                isTodayView
                  ? "flex-col min-[450px]:flex-row min-[450px]:items-center min-[450px]:justify-between"
                  : "flex-row items-center justify-between"
              }`}
            >
              <CardTitle
                className={`py-0 text-lg ${
                  isTodayView
                    ? "text-center min-[450px]:text-left"
                    : "text-left"
                }`}
              >
                {label} ({count})
              </CardTitle>

              <div
                className={`flex items-center gap-2 ${
                  isTodayView
                    ? "justify-between w-full min-[450px]:w-auto"
                    : "justify-end"
                }`}
              >
                {!activeDealStatus && activeStatus !== "pending" && (
                  <div className="relative" ref={dealStatusDropdownRef}>
                    <button
                      className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-full shadow transition-all text-sm font-medium ${
                        dateDealStatusFilter
                          ? "bg-[#00a8b4] text-white border-[#00a8b4]"
                          : "bg-white text-slate-600 border-slate-200"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDealStatusDropdownOpen((v) => !v);
                      }}
                      title="Filter by deal status"
                    >
                      <Filter size={16} />
                      <span className="max-w-[110px] truncate">
                        {dateDealStatusFilter
                          ? stageLabels[dateDealStatusFilter] || "Status"
                          : "All Status"}
                      </span>
                      <ChevronDown size={14} />
                    </button>

                    {dealStatusDropdownOpen && (
                      <div className="absolute right-[-65px] sm:right-0 top-10 z-50 w-48 bg-white border border-slate-200 rounded-xl shadow-xl py-1 max-h-72 overflow-y-auto">
                        <button
                          className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${
                            !dateDealStatusFilter
                              ? "font-semibold text-[#00a8b4]"
                              : "text-slate-700"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDateDealStatusFilter("");
                            setDealStatusDropdownOpen(false);
                          }}
                        >
                          All Status
                        </button>
                        <div className="border-t border-slate-100 my-1" />
                        {stages
                          .map((s) => ({
                            value: s.stage_key,
                            label: s.stage_label,
                          }))
                          .map((opt) => (
                            <button
                              key={opt.value}
                              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${
                                dateDealStatusFilter === opt.value
                                  ? "font-semibold text-[#00a8b4]"
                                  : "text-slate-700"
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDateDealStatusFilter(opt.value);
                                setDealStatusDropdownOpen(false);
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                )}

                <button
                  className="p-2 border rounded-full shadow bg-white"
                  onClick={() => {
                    setOpenCalendar(true);
                    setCalendarOpenedOnce(true);
                  }}
                >
                  <Calendar size={20} />
                </button>
              </div>
            </div>
          );
        })()}
      </CardHeader>
      <CardContent className="px-0">
        {!isLoading && !error && leads.length === 0 && (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <p className="text-lg font-medium">No leads are available.</p>
          </div>
        )}
        {isLoading && (
          <div className="flex items-center justify-center gap-2 py-8">
            <span className="w-2 h-2 bg-[#00a8b4] rounded-full animate-bounce [animation-delay:-0.3s]" />
            <span className="w-2 h-2 bg-[#00a8b4] rounded-full animate-bounce [animation-delay:-0.15s]" />
            <span className="w-2 h-2 bg-[#00a8b4] rounded-full animate-bounce" />
          </div>
        )}
        {error && !isLoading && (
          <div className="text-sm text-destructive">{error}</div>
        )}

        {!isLoading &&
          !error &&
          leads.map((lead) => {
            let latestComment = null;
            try {
              const comments = Array.isArray(lead.comment)
                ? lead.comment
                : JSON.parse(lead.comment || "[]");
              if (comments.length > 0)
                latestComment = comments[comments.length - 1];
            } catch {}

            return (
              <Card
                key={lead.id}
                id={`dash-lead-${lead.id}`}
                className="transition-shadow bg-white border-2 mb-2 shadow-sm cursor-pointer border-blue-100/50 rounded-xl hover:shadow-md"
                onClick={() => handleLeadClick(lead.id)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  sessionStorage.setItem(
                    DASH_SCROLL_KEY,
                    JSON.stringify({ leadId: lead.id }),
                  );
                  saveCache({ ...getSnapshot(), leads: leadsRef.current });
                  window.open(
                    `/lead/${lead.id}`,
                    "_blank",
                    "noopener,noreferrer",
                  );
                }}
              >
                <CardContent>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex items-center justify-between">
                        <h3 className="mb-1 font-semibold capitalize">
                          {lead.name || "No name"}
                        </h3>
                        <Badge
                          variant="secondary"
                          className="ml-4 capitalize whitespace-nowrap"
                        >
                          {stageLabels[lead.deal_status] ||
                            String(lead.deal_status || "").replace(/_/g, " ")}
                        </Badge>
                      </div>

                      <p className="flex gap-1 text-sm">
                        <MapPinCheckInside
                          size={16}
                          className="text-muted-foreground"
                        />
                        <span className="text-sm text-muted-foreground max-w-[250px]">
                          {lead.address || "Not specified"}
                        </span>
                      </p>

                      <div className="grid grid-cols-2">
                        <p className="flex flex-col my-1 text-sm">
                          <span className="text-xs font-medium text-muted-foreground">
                            Lead Source:
                          </span>
                          <span className="text-foreground">
                            {lead.reference || "Not specified"}
                          </span>
                        </p>
                        <p className="flex flex-col my-1 text-sm">
                          <span className="text-xs font-medium text-muted-foreground">
                            Agent:
                          </span>
                          <span className="text-foreground">
                            {lead.agent_name || "Unassigned"}
                          </span>
                        </p>
                      </div>

                      {latestComment && (
                        <p className="flex flex-col my-1 text-sm">
                          <span className="text-xs font-medium text-muted-foreground">
                            Latest Comment:
                          </span>
                          <span className="text-foreground break-words line-clamp-3">
                            {latestComment.comment_text ||
                              JSON.stringify(latestComment)}
                          </span>
                        </p>
                      )}

                      {/* Created & Last Updated badges */}
                      <div className="flex items-center gap-2 mt-1.5">
                        {lead.created_at && (
                          <span className="inline-flex items-center gap-1 text-[9px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">
                            <Calendar size={8} />
                            {formatCreatedDate(lead.created_at)}
                          </span>
                        )}
                        {lead.updated_at &&
                          lead.updated_at !== lead.created_at && (
                            <span className="inline-flex items-center gap-1 text-[9px] text-blue-400 bg-blue-50 px-1.5 py-0.5 rounded">
                              <Clock size={8} />
                              {timeAgo(lead.updated_at)}
                            </span>
                          )}
                      </div>

                      <div className="flex items-center justify-start gap-3 mt-2">
                        <button
                          className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-blue-50 border border-blue-500 hover:bg-blue-100 transition active:scale-95"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = `tel:${lead.phone_number}`;
                          }}
                          title="Call"
                        >
                          <Phone size={14} className="text-blue-600" />
                        </button>
                        <button
                          className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-emerald-50 border border-emerald-500 hover:bg-emerald-100 transition active:scale-95"
                          onClick={(e) => {
                            e.stopPropagation();

                            // Remove spaces, +, -, brackets etc.
                            let phone = lead.phone_number.replace(/\D/g, "");

                            // Add India country code if missing
                            if (phone.length === 10) {
                              phone = `91${phone}`;
                            }

                            const message = encodeURIComponent(
                              `Hi ${lead.name}`,
                            );

                            window.open(
                              `https://wa.me/${phone}?text=${message}`,
                              "_blank",
                            );
                          }}
                          title="Whatsapp"
                        >
                          <FaWhatsapp size={16} className="text-emerald-600" />
                        </button>
                        <button
                          className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-red-50 border border-red-500 hover:bg-red-100 transition active:scale-95"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = `mailto:${lead.email}`;
                          }}
                          title="Email"
                        >
                          <Mail size={14} className="text-red-600" />
                        </button>
                        <VoiceUpdateButton
                          leadId={lead.id}
                          leadName={lead.name}
                          onUpdated={() => {}}
                        />
                        <button
                          className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-emerald-50 border border-emerald-500 hover:bg-emerald-100 transition active:scale-95"
                          onClick={(e) => {
                            e.stopPropagation();
                            assignScrollLeadId.current = lead.id;
                            setSelectedLead(lead);
                            setIsAssignModalOpen(true);
                          }}
                          title="Reassign Lead"
                        >
                          <UserPlus size={14} className="text-emerald-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

        <div ref={sentinelCallbackRef} className="h-1" />

        {isFetchingMore && (
          <div className="flex justify-center py-4">
            <div className="w-5 h-5 border-2 border-[#00a8b4] border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!hasMore && leads.length > 0 && !isLoading && (
          <p className="text-center text-xs text-slate-400 pb-4">
            You've reached the end
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default DashboardLeadCard;
